import { ConsultantTableColumns, SortOrder } from "@bonliva-crm/shared";
import { SortOption as SortOptionKey } from "@search";
import { SortOption } from "./consultant-filter-state";

/**
 * Bridges the string-based sort options rendered by the sort dropdown
 * (see `@search` `getSortOptionsNew`) and the object-shaped `sort` value
 * kept in the consultant search filter state (`{ sortBy, sortOrder }`).
 *
 * The object shape is what `use-consultant-table-datasource` passes straight
 * to the GraphQL query (`sortBy: ConsultantTableColumns`, `sortOrder: SortOrder`),
 * so the mapping mirrors the backend's `SortByToMongoKey`/`toSort` handling:
 * - Name            -> alphabetical sort
 * - LastContactDate -> last contact date
 * - CreatedAt       -> latest added
 * - null            -> relevance (no explicit sort key)
 */
const sortOptionToValue: Partial<Record<SortOptionKey, SortOption>> = {
  [SortOptionKey.name_asc]: {
    sortBy: ConsultantTableColumns.Name,
    sortOrder: SortOrder.Asc
  },
  [SortOptionKey.last_contact_desc]: {
    sortBy: ConsultantTableColumns.LastContactDate,
    sortOrder: SortOrder.Desc
  },
  [SortOptionKey.last_contact_asc]: {
    sortBy: ConsultantTableColumns.LastContactDate,
    sortOrder: SortOrder.Asc
  },
  [SortOptionKey.oldest_contact_desc]: {
    sortBy: ConsultantTableColumns.CreatedAt,
    sortOrder: SortOrder.Desc
  },
  [SortOptionKey.relevance]: {
    sortBy: null,
    sortOrder: null
  }
};

export function mapSortOptionKeyToValue(key: string): SortOption {
  return (
    sortOptionToValue[key as SortOptionKey] ?? sortOptionToValue[SortOptionKey.name_asc]
  );
}

export function mapSortValueToOptionKey(value: SortOption): SortOptionKey | null {
  const match = Object.entries(sortOptionToValue).find(
    ([, candidate]) =>
      candidate.sortBy === value?.sortBy && candidate.sortOrder === value?.sortOrder
  );

  return (match?.[0] as SortOptionKey) ?? null;
}
